import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Avatar, Grid, Container, List, ListItem, ListItemText, ListItemIcon,
    useTheme, useMediaQuery, IconButton, Badge, Paper, ListItemAvatar, Button, Collapse,
    Skeleton, CircularProgress
} from '@mui/material';
import {
    Bell, Settings, Menu as MenuIcon, CreditCard, Users, Award, Check,
    ChevronDown, MessageCircle, User, LogOut
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import Sidebar from './Sidebar';
import CircleTabPanelNavigation from './CircleTabPanelNavigation';
import EnhancedProfileOverview from './EnhancedProfileOverview';
import './MyProfile.css';
import './Payment/Payment.css';
import { CircleDataProvider } from './CircleDataContext';

// Import service modules
import { authService } from '../../services/authService';
import { circleService } from '../../services/circleService';

// Background Blobs
const BackgroundBlobs = () => {
    return (
        <div className="background-blobs">
            <div className="blob blob-1"></div>
            <div className="blob blob-2"></div>
            <div className="blob blob-3"></div>
            <div className="blob blob-4"></div>
        </div>
    );
};

// Main MyProfile Component with enhanced UI
const MyProfile = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [darkMode, setDarkMode] = useState(false);
    const [showQuickSettings, setShowQuickSettings] = useState(false);
    const [notifications, setNotifications] = useState(3); // Notification count
    const [scrollPosition, setScrollPosition] = useState(0);
    const [showMoreActivity, setShowMoreActivity] = useState(false);
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [loadingUserData, setLoadingUserData] = useState(true); // Add loading state
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Auth0 hooks
    const { user: auth0User, logout, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();

    // User data - Remove hardcoded values to prevent flashing
    const [user, setUser] = useState({
        name: '',
        email: '',
        avatar: '',
        role: 'Subscription Manager',
        totalSavings: 0,
        activeCircles: 0,
        pendingInvites: 0,
        nextPayment: 0,
        nextPaymentDate: null,
        joinDate: null,
        verificationStatus: ''
    });

    // Update user data when Auth0 user is available - ENHANCED VERSION
    useEffect(() => {
        console.log("=== MYPROFILE USEEFFECT TRIGGERED ===");
        console.log("isAuthenticated:", isAuthenticated);
        console.log("auth0User:", auth0User);
        console.log("authLoading:", authLoading);
        
        if (isAuthenticated && auth0User) {
            console.log("=== STARTING USER DATA FETCH ===");
            setLoadingUserData(true); // Set loading state while fetching user info
            
            const fetchUserInfo = async () => {
                try {
                    console.log("=== FRONTEND USER DATA FETCH DEBUG ===");
                    console.log("Auth0 user data:", {
                        name: auth0User.name,
                        email: auth0User.email,
                        picture: auth0User.picture
                    });
                    
                    // Get access token from Auth0
                    const token = await getAccessTokenSilently({
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                        scope: 'openid profile email'
                    });
                    
                    console.log("Got Auth0 token, calling backend getUserInfo...");
                    
                    // Use authService to get user info from backend
                    const userInfoResponse = await authService.getUserInfo(token);
                    
                    console.log("=== BACKEND RESPONSE RECEIVED ===");
                    console.log("Full backend response:", userInfoResponse);
                    console.log("Response type:", typeof userInfoResponse);
                    console.log("Response keys:", Object.keys(userInfoResponse || {}));
                    
                    if (userInfoResponse && (userInfoResponse.data || userInfoResponse.full_name !== undefined || userInfoResponse.first_name !== undefined)) {
                        // Handle both wrapped and direct response formats
                        const backendUserData = userInfoResponse.data || userInfoResponse;
                        
                        console.log("=== PROCESSING BACKEND DATA ===");
                        console.log('Backend user data received:', backendUserData);
                        console.log('Backend first_name:', backendUserData.first_name);
                        console.log('Backend last_name:', backendUserData.last_name);
                        console.log('Backend full_name:', backendUserData.full_name);
                        console.log('Backend name_source:', backendUserData.name_source);
                        console.log('Backend email:', backendUserData.email);
                        
                        // PRIORITY 1: Use backend Django User data (most reliable)
                        let displayName = backendUserData.full_name;
                        
                        // Fallback name building if full_name is empty
                        if (!displayName || displayName.trim() === '') {
                            displayName = `${backendUserData.first_name || ''} ${backendUserData.last_name || ''}`.trim();
                            console.log('Built displayName from first+last:', displayName);
                        }
                        
                        // Final fallback
                        if (!displayName || displayName.trim() === '') {
                            displayName = 'User';
                            console.log('Using final fallback: User');
                        }
                        
                        console.log(`=== FINAL DECISION ===`);
                        console.log(`Setting user display name to: "${displayName}"`);
                        
                        // Build user object with backend data taking priority
                        const newUserData = {
                            ...user, // Keep previous state
                            // Use backend data first, then Auth0 as fallback  
                            name: displayName,
                            full_name: backendUserData.full_name,
                            first_name: backendUserData.first_name,
                            last_name: backendUserData.last_name,
                            email: backendUserData.email || auth0User.email,
                            avatar: auth0User.picture || '', // Auth0 picture is usually reliable
                            // Profile data from backend
                            phone_number: backendUserData.phone_number,
                            address_line_1: backendUserData.address_line_1,
                            address_line_2: backendUserData.address_line_2,
                            city: backendUserData.city,
                            state: backendUserData.state,
                            zip_code: backendUserData.zip_code,
                            country: backendUserData.country,
                            profile_verified: backendUserData.profile_verified,
                            stripe_verified: backendUserData.stripe_verified,
                            joinDate: backendUserData.date_joined,
                            // Keep existing user data that might not be in backend response
                            role: user.role || 'Subscription Manager',
                            totalSavings: user.totalSavings || 0,
                            activeCircles: user.activeCircles || 0,
                            pendingInvites: user.pendingInvites || 0,
                            nextPayment: user.nextPayment || 0,
                            nextPaymentDate: user.nextPaymentDate,
                            verificationStatus: backendUserData.profile_verified ? 'verified' : 'pending',
                            // DEBUG: Include backend debug info
                            _debug_name_source: backendUserData.name_source,
                            _debug_backend_data: backendUserData
                        };
                        
                        console.log("=== SETTING USER STATE ===");
                        console.log("New user data to set:", newUserData);
                        
                        setUser(newUserData);
                        
                        console.log(`SUCCESS: User display name set to: "${displayName}" (source: ${backendUserData.name_source})`);
                    } else {
                        console.warn('=== NO VALID BACKEND DATA - USING AUTH0 FALLBACK ===');
                        console.log('userInfoResponse was:', userInfoResponse);
                        
                        let auth0Name = auth0User.name || '';
                        console.log('Auth0 name field:', auth0Name);
                        
                        // Check if Auth0 name is actually an email (common issue)
                        const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(auth0Name);
                        console.log('Auth0 name is email?', isEmail);
                        
                        let displayName;
                        if (isEmail) {
                            // If Auth0 name is an email, try to extract a reasonable name
                            const emailUsername = auth0Name.split('@')[0];
                            displayName = emailUsername.replace(/[._\d]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).trim() || 'User';
                            console.log(`Auth0 name was email, extracted: "${displayName}"`);
                        } else if (auth0Name) {
                            displayName = auth0Name;
                            console.log(`Using Auth0 name: "${displayName}"`);
                        } else {
                            displayName = 'User';
                            console.log('No name available, using "User"');
                        }
                        
                        setUser(prev => ({
                            ...prev,
                            name: displayName,
                            email: auth0User.email || prev.email,
                            avatar: auth0User.picture || prev.avatar,
                            _debug_name_source: 'auth0_fallback'
                        }));
                        
                        console.log(`AUTH0 FALLBACK: Set name to "${displayName}"`);
                    }
                } catch (error) {
                    console.error('=== ERROR FETCHING USER INFO ===');
                    console.error('Error object:', error);
                    
                    // Log detailed error information
                    if (error.response) {
                        console.error('Error response status:', error.response.status);
                        console.error('Error response data:', error.response.data);
                        console.error('Error response headers:', error.response.headers);
                    } else if (error.request) {
                        console.error('Error request (no response):', error.request);
                    } else {
                        console.error('Error message:', error.message);
                    }
                    
                    // EMERGENCY FALLBACK: Process Auth0 data as best we can
                    let fallbackName = auth0User.name || '';
                    console.log('EMERGENCY: Using Auth0 name for fallback:', fallbackName);
                    
                    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fallbackName);
                    
                    if (isEmail) {
                        const emailUsername = fallbackName.split('@')[0];
                        fallbackName = emailUsername.replace(/[._\d]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).trim() || 'User';
                        console.log(`EMERGENCY: Processed email to: "${fallbackName}"`);
                    }
                    
                    setUser(prev => ({
                        ...prev,
                        name: fallbackName || 'User',
                        email: auth0User.email || prev.email,
                        avatar: auth0User.picture || prev.avatar,
                        _debug_name_source: 'emergency_fallback',
                        _debug_error: error.message
                    }));
                    
                    console.log(`EMERGENCY FALLBACK: Set name to "${fallbackName}"`);
                } finally {
                    console.log("=== USER DATA FETCH COMPLETE ===");
                    setLoadingUserData(false);
                }
            };
            
            fetchUserInfo();
        } else if (!authLoading && !isAuthenticated) {
            console.log("User not authenticated, setting loading to false");
            setLoadingUserData(false);
        } else {
            console.log("Still loading auth or waiting for auth0User");
        }
    }, [isAuthenticated, auth0User, getAccessTokenSilently, authLoading]);

    // Activity feed data - matches the image (COMMENTED OUT FOR NOW)
    /*
    const activityFeed = [
        { id: 1, type: 'payment', message: 'Successfully paid $40 for Dog Lovers circle', time: '2 hours ago', icon: 'CreditCard' },
        { id: 2, type: 'invite', message: 'James accepted your invitation to join Do More Unlimited', time: '1 day ago', icon: 'Users' },
        { id: 3, type: 'milestone', message: 'You saved over $300 with Circledin!', time: '3 days ago', icon: 'Award' },
        { id: 4, type: 'system', message: 'Your account verification is complete', time: '1 week ago', icon: 'Check' },
        // Additional activities that would be shown when "Load more" is clicked
        { id: 5, type: 'payment', message: 'Monthly payment processed for Entertainment Plus', time: '2 weeks ago', icon: 'CreditCard' },
        { id: 6, type: 'invite', message: 'You invited Sarah to join Music Unlimited', time: '3 weeks ago', icon: 'Users' },
    ];
    */

    // Helper function to format dates
    const formatShortDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Scroll handler for parallax effects
    useEffect(() => {
        const handleScroll = () => {
            setScrollPosition(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Enhanced Toggle dark mode with proper CSS variable updates
    const toggleDarkMode = () => {
        const newDarkMode = !darkMode;
        setDarkMode(newDarkMode);

        // Toggle body class for global styles
        document.body.classList.toggle('dark-mode', newDarkMode);

        // Apply CSS variable updates for components that might not inherit properly
        const root = document.documentElement;

        if (newDarkMode) {
            // Set dark mode text and background variables
            root.style.setProperty('--text-primary', '#e5e7eb');
            root.style.setProperty('--text-secondary', '#d1d5db');
            root.style.setProperty('--background-paper', 'rgba(31, 41, 55, 0.8)');
            root.style.setProperty('--background-default', '#121121');
            root.style.setProperty('--border-color', 'rgba(75, 85, 99, 0.3)');

            // Set dark mode component-specific variables
            root.style.setProperty('--tier-card-bg', 'rgba(31, 41, 55, 0.7)');
            root.style.setProperty('--tier-card-current', 'rgba(99, 102, 241, 0.15)');
            root.style.setProperty('--tier-card-next', 'rgba(56, 189, 248, 0.15)');
            root.style.setProperty('--icon-bg', 'rgba(99, 102, 241, 0.2)');
            root.style.setProperty('--metrics-bg', 'rgba(31, 41, 55, 0.7)');

            // Set brighter colors for better visibility in dark mode
            root.style.setProperty('--primary', '#818cf8');
            root.style.setProperty('--primary-light', '#a5b4fc');
            root.style.setProperty('--secondary', '#38bdf8');
            root.style.setProperty('--secondary-light', '#7dd3fc');
            root.style.setProperty('--success', '#4ade80');
            root.style.setProperty('--warning', '#fbbf24');
            root.style.setProperty('--error', '#f87171');
        } else {
            // Reset to light mode defaults
            root.style.setProperty('--text-primary', '#1A1B25');
            root.style.setProperty('--text-secondary', 'rgba(26, 27, 37, 0.7)');
            root.style.setProperty('--background-paper', 'rgba(255, 255, 255, 0.8)');
            root.style.setProperty('--background-default', '#f8f9fd');
            root.style.setProperty('--border-color', 'rgba(229, 231, 235, 0.8)');

            // Reset component-specific variables
            root.style.setProperty('--tier-card-bg', 'white');
            root.style.setProperty('--tier-card-current', 'rgba(238, 242, 255, 1)');
            root.style.setProperty('--tier-card-next', 'rgba(224, 242, 254, 0.8)');
            root.style.setProperty('--icon-bg', 'rgba(99, 102, 241, 0.1)');
            root.style.setProperty('--metrics-bg', 'rgba(255, 255, 255, 0.9)');

            // Reset to regular light mode colors
            root.style.setProperty('--primary', '#6366f1');
            root.style.setProperty('--primary-light', '#818cf8');
            root.style.setProperty('--secondary', '#0ea5e9');
            root.style.setProperty('--secondary-light', '#38bdf8');
            root.style.setProperty('--success', '#10b981');
            root.style.setProperty('--warning', '#f59e0b');
            root.style.setProperty('--error', '#ef4444');
        }
    };

    // Handle user menu open/close
    const handleUserMenuOpen = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    // Handle logout
    const handleLogout = () => {
        handleUserMenuClose();
        logout({ returnTo: window.location.origin });
    };

    // Helper function to get the icon component (COMMENTED OUT FOR NOW)
    /*
    const getActivityIcon = (iconName) => {
        switch (iconName) {
            case 'CreditCard':
                return <CreditCard size={20} />;
            case 'Users':
                return <Users size={20} />;
            case 'Award':
                return <Award size={20} />;
            case 'Check':
                return <Check size={20} />;
            default:
                return <MessageCircle size={20} />;
        }
    };

    // Helper function to get the icon background color (COMMENTED OUT FOR NOW)
    const getIconBackground = (type) => {
        switch (type) {
            case 'payment':
                return 'rgba(76, 175, 80, 0.1)';
            case 'invite':
                return 'rgba(33, 150, 243, 0.1)';
            case 'milestone':
                return 'rgba(255, 193, 7, 0.1)';
            case 'system':
                return 'rgba(158, 158, 158, 0.1)';
            default:
                return 'rgba(158, 158, 158, 0.1)';
        }
    };

    // Helper function to get the icon color (COMMENTED OUT FOR NOW)
    const getIconColor = (type) => {
        switch (type) {
            case 'payment':
                return '#4CAF50';
            case 'invite':
                return '#2196F3';
            case 'milestone':
                return '#FFC107';
            case 'system':
                return '#9E9E9E';
            default:
                return '#9E9E9E';
        }
    };
    */

    // Generate a fallback avatar URL with initials - ENHANCED VERSION
    const getFallbackAvatarUrl = () => {
        const fullName = user?.name || user?.full_name || '';
        let displayName = 'User';

        if (fullName) {
            // Check if it looks like an email (safety check)
            if (fullName.includes('@')) {
                const emailUsername = fullName.split('@')[0];
                displayName = emailUsername.replace(/[._\d]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).trim() || 'User';
            } else {
                displayName = fullName;
            }
        }

        return `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=random`;
    };

    // Add CSS for spin animation
    const styles = `
        @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
        }
        
        .spin-animation {
            animation: spin 1.5s linear infinite;
        }
    `;

    // Render loading state if user data is still loading
    if (loadingUserData || authLoading) {
        return (
            <Box
                className={`my-profile myprofile-main-content ${darkMode ? 'dark-mode' : ''}`}
                sx={{
                    display: 'flex',
                    minHeight: '100vh',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <div className="noise-overlay"></div>
                <BackgroundBlobs />
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={60} sx={{ color: '#FF4775', mb: 2 }} />
                    <Typography variant="h6" sx={{ color: darkMode ? 'white' : 'inherit' }}>
                        Loading your profile...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <>
            {/* Add styles for spin animation */}
            <style>{styles}</style>
            <Box
                className={`my-profile myprofile-main-content ${darkMode ? 'dark-mode' : ''}`}
                sx={{ display: 'flex', minHeight: '100vh' }}
            >
                <div className="noise-overlay"></div>
                <BackgroundBlobs />

                {/* Sidebar Component */}
                <Sidebar
                    isOpen={sidebarOpen}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    darkMode={darkMode}
                />

                {/* Main Content wrapped with CircleDataProvider */}
                <CircleDataProvider>
                    <Box
                        component="main"
                        sx={{
                            flexGrow: 1,
                            p: { xs: 0, sm: 2, md: 3 }, // ZERO padding on mobile
                            width: { xs: '100%', sm: `calc(100% - ${sidebarOpen ? 240 : 80}px)` },
                            ml: { xs: 0, sm: `${sidebarOpen ? 240 : 80}px` },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            zIndex: 2
                        }}
                    >
                        <Container
                            maxWidth="lg"
                            className="mobile-container"
                            sx={{
                                position: 'relative',
                                zIndex: 2,
                                pt: { xs: 0, sm: showQuickSettings ? 6 : 1 },
                                px: { xs: 0, sm: 2 }, // ZERO horizontal padding on mobile
                                width: { xs: '100%', sm: 'auto' }, // Full width on mobile
                                maxWidth: { xs: '100%', sm: 'lg' }, // Override maxWidth on mobile
                                m: { xs: 0 } // Remove margin on mobile
                            }}
                        >
                            {/* Enhanced Profile Overview - Self-contained */}
                            <EnhancedProfileOverview
                                user={user}
                                darkMode={darkMode}
                                onThemeChange={setDarkMode} // Pass the dark mode setter
                                formatShortDate={formatShortDate}
                            />

                            {/* Main Content Area with TabPanel Navigation */}
                            <Box sx={{ mb: 4, px: { xs: 2, sm: 0 } }} className="main-content-area">
                                {/* Subscription Circles Title - CHANGED: Removed dark mode button */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        className="content-title"
                                        sx={{ color: 'var(--text-primary)' }} // Use CSS variable for text color
                                    >
                                        Your Subscription Circles
                                    </Typography>
                                </Box>

                                {/* CircleTabPanelNavigation */}
                                <Box className="circle-tabs-container">
                                    <CircleTabPanelNavigation darkMode={darkMode} />
                                </Box>

                                {/* RECENT ACTIVITY SECTION - COMMENTED OUT FOR LATER DEVELOPMENT */}
                                {/*
                                <Box sx={{ mt: { xs: 4, sm: 6 } }}>
                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                        sx={{
                                            mb: 2,
                                            color: 'var(--text-primary)' // Use CSS variable for text color
                                        }}
                                    >
                                        Recent Activity
                                    </Typography>
                                    <Paper
                                        sx={{
                                            borderRadius: { xs: '16px', sm: '24px' }, // Smaller radius on mobile
                                            overflow: 'hidden',
                                            background: darkMode ? 'rgba(32, 33, 44, 0.35)' : 'rgba(255, 255, 255, 0.9)',
                                            backdropFilter: 'blur(16px)',
                                            WebkitBackdropFilter: 'blur(16px)',
                                            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
                                            boxShadow: darkMode ? '0 10px 30px rgba(0, 0, 0, 0.25)' : '0 10px 30px rgba(0, 0, 0, 0.1)'
                                        }}
                                        className="activity-paper"
                                    >
                                        <List className="activity-feed">
                                            {activityFeed.slice(0, showMoreActivity ? activityFeed.length : 4).map((activity, index) => {
                                                return (
                                                    <ListItem
                                                        key={activity.id}
                                                        component={motion.li}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: index * 0.1 }}
                                                        sx={{
                                                            py: { xs: 1.5, sm: 2 }, // Less padding on mobile
                                                            px: { xs: 2, sm: 3 }, // Less padding on mobile
                                                            borderBottom: index < (showMoreActivity ? activityFeed.length - 1 : 3) ? '1px solid' : 'none',
                                                            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
                                                            transition: 'background-color 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar
                                                                sx={{
                                                                    bgcolor: getIconBackground(activity.type),
                                                                    color: getIconColor(activity.type),
                                                                    width: { xs: 36, sm: 40 }, // Smaller on mobile
                                                                    height: { xs: 36, sm: 40 } // Smaller on mobile
                                                                }}
                                                            >
                                                                {getActivityIcon(activity.icon)}
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={activity.message}
                                                            secondary={activity.time}
                                                            primaryTypographyProps={{
                                                                fontWeight: 500,
                                                                sx: {
                                                                    color: 'var(--text-primary)',
                                                                    fontSize: { xs: '0.875rem', sm: '1rem' } // Smaller font on mobile
                                                                }
                                                            }}
                                                            secondaryTypographyProps={{
                                                                sx: { color: 'var(--text-secondary)' } // Use CSS variable for secondary text color
                                                            }}
                                                        />
                                                    </ListItem>
                                                );
                                            })}
                                        </List>

                                        <Box p={{ xs: 1.5, sm: 2 }} display="flex" justifyContent="center">
                                            <Button
                                                variant="outlined"
                                                endIcon={<ChevronDown size={16} />}
                                                onClick={() => setShowMoreActivity(!showMoreActivity)}
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: '12px',
                                                    borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                                    color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                                                    py: { xs: 0.75, sm: 1 }, // Smaller padding on mobile
                                                    px: { xs: 2, sm: 2.5 } // Smaller padding on mobile
                                                }}
                                            >
                                                {showMoreActivity ? 'Show less activity' : 'Load more activity'}
                                            </Button>
                                        </Box>
                                    </Paper>
                                </Box>
                                */}
                            </Box>
                        </Container>
                    </Box>
                </CircleDataProvider>
            </Box>
        </>
    );
};

export default MyProfile;

/* 

Working on March 12 11am before updates

import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Avatar, Grid, Container, List, ListItem, ListItemText, ListItemIcon,
    useTheme, useMediaQuery, IconButton, Tab, Tabs, Card, CardContent, CardActions, Chip, Button,
    Collapse, Tooltip, ListItemAvatar, Fab, TextField, InputAdornment, CircularProgress, Paper,
    LinearProgress, Switch, Menu, MenuItem, Modal, Divider, Badge
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Calendar, HelpCircle, User, Shield, Users, Star, TrendingUp,
    TrendingDown, Award, Search, Sun, Moon, Bell, Settings, X, Percent,
    DollarSign, Plus, CreditCard, Zap, Check, Clock, ChevronRight, ArrowRight,
    ChevronDown, Menu as MenuIcon, Heart, Eye, Bookmark, Activity, Gift, MessageCircle, Crown
} from 'lucide-react';
import { AlertTriangle } from "lucide-react";
import { motion, useMotionValue, useTransform, AnimatePresence, useSpring, useAnimation } from 'framer-motion';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Sidebar from './Sidebar';
import CircleTabPanelNavigation from './CircleTabPanelNavigation';
import './MyProfile.css';
import './Payment/Payment.css'

// Styled components with enhanced visual effects
const GlassPaper = styled(motion.div)(({ theme, glassLevel = 'medium' }) => {
    const glassSettings = {
        light: {
            bg: 'rgba(255, 255, 255, 0.08)',
            blur: '10px',
            border: 'rgba(255, 255, 255, 0.12)'
        },
        medium: {
            bg: 'rgba(255, 255, 255, 0.1)',
            blur: '16px',
            border: 'rgba(255, 255, 255, 0.15)'
        },
        heavy: {
            bg: 'rgba(255, 255, 255, 0.12)',
            blur: '25px',
            border: 'rgba(255, 255, 255, 0.2)'
        }
    };

    const settings = glassSettings[glassLevel];

    return {
        position: 'relative',
        background: settings.bg,
        backdropFilter: `blur(${settings.blur})`,
        WebkitBackdropFilter: `blur(${settings.blur})`,
        borderRadius: '24px',
        border: `1px solid ${settings.border}`,
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '40%',
            background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))',
            zIndex: 0,
            pointerEvents: 'none'
        },
        '& > *': {
            position: 'relative',
            zIndex: 1
        }
    };
});

const GradientButton = styled(Button)(({ theme, color = 'primary', size = 'medium' }) => {
    const gradients = {
        primary: 'linear-gradient(45deg, #FF4775 30%, #FF7676 90%)',
        secondary: 'linear-gradient(45deg, #3ACCE1 30%, #00BFFF 90%)',
        success: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
        warning: 'linear-gradient(45deg, #FF9800 30%, #FFC107 90%)',
    };

    const sizes = {
        small: { px: 2, py: 0.5, fontSize: '0.75rem', borderRadius: '12px' },
        medium: { px: 3, py: 1, fontSize: '0.875rem', borderRadius: '16px' },
        large: { px: 4, py: 1.5, fontSize: '1rem', borderRadius: '20px' },
    };

    const sizeProps = sizes[size];

    return {
        background: gradients[color],
        border: 0,
        color: 'white',
        padding: `${sizeProps.py}rem ${sizeProps.px}rem`,
        borderRadius: sizeProps.borderRadius,
        fontSize: sizeProps.fontSize,
        fontWeight: 600,
        letterSpacing: '0.5px',
        transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
        position: 'relative',
        overflow: 'hidden',
        textTransform: 'none',
        '&:hover': {
            boxShadow: '0 6px 20px rgba(255, 71, 117, 0.3)',
            transform: 'translateY(-3px)',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '200%',
            height: '100%',
            background: 'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0) 100%)',
            transform: 'translateX(-100%)',
            transition: 'transform 0.6s ease',
        },
        '&:hover::after': {
            transform: 'translateX(100%)',
        }
    };
});

// Custom components with modern effects
const FloatingCard = ({ children, delay = 0, hoverScale = 1.03, ...props }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                type: "spring",
                damping: 20,
                stiffness: 300,
                delay
            }}
            whileHover={{ scale: hoverScale, y: -5 }}
            className="floating-card"
            {...props}
        >
            {children}
        </motion.div>
    );
};

// Interactive 3D card with mouse tracking
const InteractiveCard = ({ children, depth = 10, glassLevel = 'medium', className = '' }) => {
    const cardRef = useRef(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);

    const handleMouseMove = (e) => {
        if (!cardRef.current) return;

        const rect = cardRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;

        setMousePosition({ x, y });
    };

    const springConfig = { damping: 25, stiffness: 300 };
    const rotateX = useSpring(0, springConfig);
    const rotateY = useSpring(0, springConfig);
    const scale = useSpring(1, springConfig);

    useEffect(() => {
        rotateX.set(isHovering ? -mousePosition.y * depth : 0);
        rotateY.set(isHovering ? mousePosition.x * depth : 0);
        scale.set(isHovering ? 1.02 : 1);
    }, [mousePosition, isHovering, depth, rotateX, rotateY, scale]);

    return (
        <motion.div
            ref={cardRef}
            className={`interactive-card ${className}`}
            style={{
                rotateX,
                rotateY,
                scale,
                transformPerspective: 1200,
                transformStyle: "preserve-3d"
            }}
            onMouseMove={handleMouseMove}
            onHoverStart={() => setIsHovering(true)}
            onHoverEnd={() => setIsHovering(false)}
        >
            <GlassPaper
                glassLevel={glassLevel}
                style={{
                    transformStyle: "preserve-3d",
                    transition: "box-shadow 0.3s ease",
                    boxShadow: isHovering
                        ? '0 20px 40px rgba(0, 0, 0, 0.2), 0 0 30px rgba(255, 71, 117, 0.12)'
                        : '0 10px 30px rgba(0, 0, 0, 0.1)'
                }}
            >
                <div className="card-shine"></div>
                <div className="card-content">
                    {children}
                </div>
            </GlassPaper>
        </motion.div>
    );
};

// Background Blobs
const BackgroundBlobs = () => {
    return (
        <div className="background-blobs">
            <div className="blob blob-1"></div>
            <div className="blob blob-2"></div>
            <div className="blob blob-3"></div>
            <div className="blob blob-4"></div>
        </div>
    );
};

// Main MyProfile Component with enhanced UI
const MyProfile = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [darkMode, setDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [favorites, setFavorites] = useState([1]); // Default favorite
    const [showQuickSettings, setShowQuickSettings] = useState(false);
    const [notifications, setNotifications] = useState(3); // Notification count
    const [scrollPosition, setScrollPosition] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // User data (would come from API in real app)
    const [user, setUser] = useState({
        name: 'Dan Nguyen',
        email: 'danguyen343@gmail.com',
        avatar: '/uploads/profile/dan.jpg', // This would be a real path in production
        role: 'Subscription Manager',
        totalSavings: 350,
        activeCircles: 2,
        pendingInvites: 3,
        nextPayment: 40,
        nextPaymentDate: '2023-05-15',
        joinDate: '2023-01-10',
        verificationStatus: 'verified'
    });

    // Activity feed data
    const activityFeed = [
        { id: 1, type: 'payment', message: 'Successfully paid $40 for Dog Lovers circle', time: '2 hours ago', icon: CreditCard },
        { id: 2, type: 'invite', message: 'James accepted your invitation to join Do More Unlimited', time: '1 day ago', icon: Users },
        { id: 3, type: 'milestone', message: 'You saved over $300 with Circledin!', time: '3 days ago', icon: Award },
        { id: 4, type: 'system', message: 'Your account verification is complete', time: '1 week ago', icon: Check },
    ];

    // Scroll handler for parallax effects
    useEffect(() => {
        const handleScroll = () => {
            setScrollPosition(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Toggle dark mode
    const toggleDarkMode = () => {
        setDarkMode(!darkMode);
        document.body.classList.toggle('dark-mode');
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                className={`my-profile myprofile-main-content ${darkMode ? 'dark-mode' : ''}`}
                sx={{ display: 'flex', minHeight: '100vh' }}
            >
                <div className="noise-overlay"></div>
                <BackgroundBlobs />

                
                <Sidebar
                    isOpen={sidebarOpen}
                    onToggle={() => setSidebarOpen(!sidebarOpen)}
                    darkMode={darkMode}
                />

                
                <Box
                    className={`myprofile-header ${!sidebarOpen ? 'collapsed' : ''}`}
                    style={{
                        transform: `translateY(${scrollPosition * 0.1}px)`,
                        opacity: scrollPosition > 100 ? 0.95 : 1
                    }}
                >
                    <Box display="flex" alignItems="center">
                        {isMobile && (
                            <IconButton onClick={() => setSidebarOpen(!sidebarOpen)} sx={{ mr: 1 }}>
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Typography
                            variant="h6"
                            className="myprofile-text-gradient"
                            sx={{
                                flexGrow: 1,
                                display: { xs: 'none', md: 'block' }
                            }}
                        >
                            My Dashboard
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <IconButton
                            onClick={toggleDarkMode}
                            className="myprofile-dark-mode-toggle"
                            sx={{
                                background: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'
                                }
                            }}
                        >
                            <motion.div
                                animate={{ rotate: darkMode ? 180 : 0 }}
                                transition={{ duration: 0.5, type: 'spring' }}
                            >
                                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
                            </motion.div>
                        </IconButton>

                        <IconButton
                            sx={{
                                background: 'rgba(255,71,117,0.08)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(255,71,117,0.15)'
                                }
                            }}
                        >
                            <Badge badgeContent={notifications} color="error">
                                <Bell size={20} />
                            </Badge>
                        </IconButton>

                        <IconButton
                            onClick={() => setShowQuickSettings(!showQuickSettings)}
                            sx={{
                                background: showQuickSettings ? 'rgba(255,71,117,0.15)' : 'rgba(0,0,0,0.05)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'rgba(0,0,0,0.1)'
                                }
                            }}
                        >
                            <Settings size={20} />
                        </IconButton>

                        <Tooltip title={user.name}>
                            <Avatar
                                src={user.avatar}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    border: '2px solid #FF4775',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        boxShadow: '0 0 0 2px rgba(255,71,117,0.5)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                            />
                        </Tooltip>
                    </Box>
                </Box>

                
                <Collapse in={showQuickSettings}>
                    <Box
                        className="myprofile-quick-settings"
                        component={motion.div}
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <Typography variant="h6" gutterBottom fontWeight={600}>Quick Settings</Typography>
                        <Divider sx={{ mb: 2 }} />

                        <List disablePadding>
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Moon size={18} />
                                </ListItemIcon>
                                <ListItemText primary="Dark Mode" />
                                <Switch checked={darkMode} onChange={toggleDarkMode} />
                            </ListItem>

                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Bell size={18} />
                                </ListItemIcon>
                                <ListItemText primary="Notifications" />
                                <Switch defaultChecked />
                            </ListItem>

                            <ListItem disablePadding>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Activity size={18} />
                                </ListItemIcon>
                                <ListItemText primary="Activity Feed" />
                                <Switch defaultChecked />
                            </ListItem>
                        </List>
                    </Box>
                </Collapse>

                
                <Box
                    component="main"
                    sx={{
                        flexGrow: 1,
                        p: 3,
                        width: { sm: `calc(100% - ${sidebarOpen ? 240 : 80}px)` },
                        ml: { sm: `${sidebarOpen ? 240 : 80}px` },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Container
                        maxWidth="lg"
                        sx={{
                            position: 'relative',
                            zIndex: 2,
                            pt: showQuickSettings ? 6 : 1
                        }}
                    >
                        
                        <Box className="myprofile-welcome-section myprofile-fade-in" mb={6}>
                            <InteractiveCard depth={5} glassLevel="heavy" className="welcome-card">
                                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'center', md: 'flex-start' }} p={3}>
                                    
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        pr={{ xs: 0, md: 4 }}
                                        pb={{ xs: 3, md: 0 }}
                                        position="relative"
                                    >
                                        <Badge
                                            overlap="circular"
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                            badgeContent={
                                                <Box
                                                    className="verification-badge"
                                                    component={motion.div}
                                                    whileHover={{ scale: 1.2, rotate: 10 }}
                                                >
                                                    <Check size={12} />
                                                </Box>
                                            }
                                        >
                                            <Avatar
                                                src={user.avatar}
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    border: '3px solid',
                                                    borderColor: 'primary.main',
                                                    boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                                                }}
                                            />
                                        </Badge>

                                        <Typography variant="h6" mt={2} fontWeight={600}>
                                            {user.name}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            {user.role}
                                        </Typography>
                                    </Box>

                                    
                                    <Box flexGrow={1}>
                                        <Typography variant="h5" className="myprofile-text-gradient" fontWeight={700} mb={2} textAlign={{ xs: 'center', md: 'left' }}>
                                            Welcome back, {user.name.split(' ')[0]}!
                                        </Typography>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <Box className="welcome-stat-box">
                                                    <DollarSign size={16} className="stat-icon money" />
                                                    <Box ml={1}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Next Payment Estimate
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={600}>
                                                            ${user.nextPayment}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Due {new Date(user.nextPaymentDate).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <Box className="welcome-stat-box">
                                                    <Users size={16} className="stat-icon users" />
                                                    <Box ml={1}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Active Circles
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={600}>
                                                            {user.activeCircles}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Saving on {user.activeCircles} plans
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>

                                            <Grid item xs={12} md={4}>
                                                <Box className="welcome-stat-box">
                                                    <Percent size={16} className="stat-icon percent" />
                                                    <Box ml={1}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Total Savings
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={600}>
                                                            ${user.totalSavings}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Since {new Date(user.joinDate).toLocaleDateString()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            </InteractiveCard>
                        </Box>

                        
                        <Box sx={{ mb: 4 }} className="main-content-area">
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" fontWeight={600}>Your Subscription Circles</Typography>
                            </Box>
                            
                            
                            <Box className="circle-tabs-container">
                                <CircleTabPanelNavigation />
                            </Box>
                            
                            
                            <Box sx={{ mt: 6 }}>
                                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Recent Activity</Typography>
                                <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                                    <List className="activity-feed">
                                        {activityFeed.map((activity, index) => {
                                            const Icon = activity.icon;

                                            return (
                                                <ListItem
                                                    key={activity.id}
                                                    component={motion.li}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: index * 0.1 }}
                                                    sx={{
                                                        py: 2,
                                                        px: 3,
                                                        borderBottom: index < activityFeed.length - 1 ? '1px solid' : 'none',
                                                        borderColor: 'divider'
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            sx={{
                                                                bgcolor:
                                                                    activity.type === 'payment' ? 'rgba(76, 175, 80, 0.1)' :
                                                                        activity.type === 'invite' ? 'rgba(33, 150, 243, 0.1)' :
                                                                            activity.type === 'milestone' ? 'rgba(255, 193, 7, 0.1)' :
                                                                                'rgba(158, 158, 158, 0.1)',
                                                                color:
                                                                    activity.type === 'payment' ? '#4CAF50' :
                                                                        activity.type === 'invite' ? '#2196F3' :
                                                                            activity.type === 'milestone' ? '#FFC107' :
                                                                                '#9E9E9E',
                                                            }}
                                                        >
                                                            <Icon size={20} />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={activity.message}
                                                        secondary={activity.time}
                                                        primaryTypographyProps={{
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>

                                    <Box p={2} display="flex" justifyContent="center">
                                        <Button
                                            variant="outlined"
                                            endIcon={<ChevronDown size={16} />}
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '12px'
                                            }}
                                        >
                                            Load more activity
                                        </Button>
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    </Container>
                </Box>

                
                <Box
                    component={motion.div}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5, type: 'spring' }}
                    sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}
                >
                    <Fab
                        color="primary"
                        aria-label="add"
                        className="myprofile-fab"
                        sx={{
                            background: 'linear-gradient(45deg, #FF4775, #FF7676)',
                            color: 'white',
                            boxShadow: '0 4px 20px rgba(255, 71, 117, 0.4)',
                        }}
                    >
                        <Plus />
                    </Fab>
                </Box>
            </Box>
        </>
    );
};

export default MyProfile;



*/